import React from 'react';
import { Facebook, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_55daa337-b083-4bba-81d0-04d8a52c32f6/artifacts/6cgwa6q5_DODIPA_logo_380x380.jpg";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (e, href) => {
    e.preventDefault();
    if (href === '#top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#070b14] border-t border-white/5 pb-24 md:pb-0">
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <a 
              href="#top" 
              onClick={(e) => scrollToSection(e, '#top')}
              className="flex items-center gap-3 mb-6"
              data-testid="footer-logo"
            >
              <img 
                src={LOGO_URL} 
                alt="DODIPA Logo" 
                className="h-10 w-10 rounded-lg object-contain bg-white"
              />
              <span className="font-heading font-bold text-xl text-white">DODIPA</span>
            </a>
            <p className="text-white/50 text-sm leading-relaxed mb-6">
              Consulenza energetica e finanza agevolata per microbusiness, PMI e aziende energivore. 
              Trasformiamo l'energia in opportunità di crescita.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-white/50 hover:bg-[#1877F2] hover:text-white transition-all"
                data-testid="social-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-white/50 hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:text-white transition-all"
                data-testid="social-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">Link Rapidi</h4>
            <ul className="space-y-3">
              {[
                { href: '#servizi', label: 'Servizi' },
                { href: '#perche-noi', label: 'Perché Noi' },
                { href: '#chi-siamo', label: 'Chi Siamo' },
                { href: '#contatti', label: 'Contatti' }
              ].map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href} 
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-white/50 hover:text-[#5CBDB0] transition-colors text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">Servizi</h4>
            <ul className="space-y-3">
              {[
                'Consulenza Energetica',
                'Finanza Agevolata',
                'Monitoraggio Consumi',
                'Pratiche Autorizzative',
                'Transizione Ecologica'
              ].map((service, index) => (
                <li key={index}>
                  <span className="text-white/50 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">Contatti</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#5CBDB0] mt-1 flex-shrink-0" />
                <span className="text-white/50 text-sm">
                  Corso Pietro Giannone 1/A<br />
                  71121 Foggia (FG)
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#5CBDB0] flex-shrink-0" />
                <a href="tel:+393453509743" className="text-white/50 hover:text-[#5CBDB0] text-sm transition-colors">
                  +39 345 350 9743
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#5CBDB0] flex-shrink-0" />
                <a href="mailto:info@dodipa.it" className="text-white/50 hover:text-[#5CBDB0] text-sm transition-colors">
                  info@dodipa.it
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Section */}
        <div className="mt-16 pt-8 border-t border-white/5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Company Info */}
            <div className="text-white/40 text-xs space-y-2">
              <p data-testid="footer-company-name">
                <strong className="text-white/60">F&P IDEA S.R.L.S.</strong> - Marchio DODIPA in concessione esclusiva
              </p>
              <p data-testid="footer-vat">
                P.IVA / C.F.: 04328400710
              </p>
              <p data-testid="footer-rea">
                REA: FG-318897 | Capitale Sociale: € 1.000,00 i.v.
              </p>
              <p data-testid="footer-address">
                Sede Legale: Corso Pietro Giannone 1/A, 71121 Foggia (FG)
              </p>
              <p data-testid="footer-pec">
                PEC: fpidea@pec.it | Email: info@dodipa.it
              </p>
              <p data-testid="footer-ateco">
                Codice ATECO: 35.40.00 - Servizi di intermediazione per l'energia elettrica e il gas naturale
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 lg:justify-end text-xs">
              <Link 
                to="/privacy-policy"
                className="text-white/40 hover:text-white/70 transition-colors"
                data-testid="footer-privacy"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/cookie-policy"
                className="text-white/40 hover:text-white/70 transition-colors"
                data-testid="footer-cookie"
              >
                Cookie Policy
              </Link>
              <Link 
                to="/termini-condizioni"
                className="text-white/40 hover:text-white/70 transition-colors"
                data-testid="footer-terms"
              >
                Termini e Condizioni
              </Link>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/30 text-xs" data-testid="footer-copyright">
              © {currentYear} F&P IDEA S.R.L.S. - Tutti i diritti riservati
            </p>
            <p className="text-white/30 text-xs">
              Conforme al Regolamento UE 2016/679 (GDPR) e alla normativa italiana sulla privacy
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
